import React, { useEffect, useState, useRef, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import { ChevronDown } from "lucide-react";
import sessionStorage from "../utils/sessionStore";
import { dedupeSecondary } from "../utils/fieldHelpers";

const API_URL = process.env.REACT_APP_API_URL;

// Resolves a data path like "territories[0].salesTRPs" against a JSON object.
export function resolveDataPath(obj, path) {
  if (!obj || !path) return undefined;
  const tokens = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current = obj;
  for (const token of tokens) {
    if (current == null) return undefined;
    if (/^\d+$/.test(token)) {
      current = Array.isArray(current) ? current[parseInt(token, 10)] : undefined;
    } else {
      current = current[token];
    }
  }
  return current;
}

// Look up a property on `obj` case-insensitively. Existing DB configs use PascalCase
// (e.g. "TerritoryID") while .NET serialises camelCase ("territoryID") — supporting
// both keeps old and new configs working.
function pickField(obj, key) {
  if (!obj || !key) return undefined;
  if (obj[key] !== undefined) return obj[key];
  const target = key.toLowerCase();
  for (const k of Object.keys(obj)) {
    if (k.toLowerCase() === target) return obj[k];
  }
  return undefined;
}

// secondaryField may be a string or an array — same shape used in the dropdown.
// Returns a comma-joined display string, or "" if nothing resolves.
function extractSecondary(item, secondaryField) {
  if (!item || !secondaryField) return "";
  if (Array.isArray(secondaryField)) {
    return secondaryField
      .map((k) => pickField(item, k))
      .filter((v) => v !== undefined && v !== null && v !== "")
      .join(", ");
  }
  const v = pickField(item, secondaryField);
  return v == null ? "" : String(v);
}

/**
 * DB-driven autocomplete input. FieldOptions JSON shape:
 *   {
 *     "apiUrl":         "/api/Customer",
 *     "queryParam":     "filter",
 *     "queryTemplate":  "startswith(CustID,'{value}')",  // optional — wraps the typed value
 *     "defaultParams":  "Top=10",                         // optional — extra static query
 *     "dataPath":       "customers",
 *     "displayField":   "custID",
 *     "valueField":     "custID",
 *     "secondaryField": "name",                           // optional — shown next to display
 *     "minChars":       1
 *   }
 */
function AutocompleteField({ config, value, onChange, onSelectItem, disabled, placeholder, required, size, maxLength, inputWidth, variant, label }) {
  const isCard = variant === "card";
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  // Tracks whether the input has focus so the committed code+description overlay
  // (non-card) shows only when the field isn't being edited.
  const [isFocused, setIsFocused] = useState(false);
  // Description shown beside the input once a value is locked in. Sourced
  // from the picked item on selection, or auto-fetched for an existing value
  // in edit mode (single GET, deduped by lastFetchedForRef).
  const [displayedSecondary, setDisplayedSecondary] = useState("");
  const lastFetchedForRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setInputValue(value || ""); }, [value]);

  // Auto-populate the description when `value` is set externally (edit mode)
  // and we haven't fetched for this value yet. One-shot per value; cleared
  // below when the user starts editing the field.
  useEffect(() => {
    if (!value || !config?.secondaryField || !config?.apiUrl) {
      setDisplayedSecondary("");
      return;
    }
    if (lastFetchedForRef.current === value) return;

    // queryTemplate may reference {value} more than once (e.g.
    // "startswith(CustID,'{value}') or contains(Name,'{value}')") — a plain
    // .replace() only swaps the first match, leaving the second as a literal
    // "{value}" that the backend then matches nothing against. Substitute
    // every occurrence with a global regex.
    const queryValue = config.queryTemplate
      ? config.queryTemplate.replace(/\{value\}/g, value)
      : value;
    const defaultParams = config.defaultParams ? `&${config.defaultParams}` : "";
    const url = `${API_URL}${config.apiUrl}?${config.queryParam}=${encodeURIComponent(queryValue)}${defaultParams}&Top=10`;

    const headers = {};
    const token = sessionStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    lastFetchedForRef.current = value;

    fetch(url, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        const items = resolveDataPath(data, config.dataPath) || [];
        const needle = String(value).toLowerCase();
        const exact = items.find(
          (it) => String(pickField(it, config.valueField) ?? "").toLowerCase() === needle
        );
        if (exact) setDisplayedSecondary(extractSecondary(exact, config.secondaryField));
      })
      .catch(() => { /* silent — counter is best-effort */ });
  }, [value, config]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback((query, force = false) => {
    if (!force && (!query || query.length < (config.minChars || 1))) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    // Same global substitution as in the description auto-fetch above —
    // queryTemplate can repeat {value}.
    const queryValue = config.queryTemplate
      ? config.queryTemplate.replace(/\{value\}/g, query)
      : query;
    const defaultParams = config.defaultParams ? `&${config.defaultParams}` : "";
    const url = `${API_URL}${config.apiUrl}?${config.queryParam}=${encodeURIComponent(queryValue)}${defaultParams}&Top=10`;

    const headers = {};
    const token = sessionStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(url, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        let items = resolveDataPath(data, config.dataPath) || [];
        // Client-side regex filter — used e.g. to limit ParentTerritory_c
        // suggestions to top-level territories only (no "-" in the ID).
        if (config.clientFilter?.field && config.clientFilter?.matches) {
          let re = null;
          try { re = new RegExp(config.clientFilter.matches); } catch { /* ignore */ }
          if (re) {
            items = items.filter((it) => {
              const v = pickField(it, config.clientFilter.field);
              return v != null && re.test(String(v));
            });
          }
        }
        setSuggestions(items);
        setIsOpen(items.length > 0);
        setHighlightIdx(-1);
      })
      .catch(() => { setSuggestions([]); setIsOpen(false); })
      .finally(() => setIsLoading(false));
  }, [config]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    // User is typing — the previous selection no longer matches; clear the
    // description until they pick a new item (or clear the field).
    setDisplayedSecondary("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (item) => {
    const val = pickField(item, config.valueField) ?? "";
    setInputValue(String(val));
    onChange(String(val));
    setDisplayedSecondary(extractSecondary(item, config.secondaryField));
    lastFetchedForRef.current = String(val); // prevent re-fetch loop
    onSelectItem?.(item);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIdx]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Card variant opens the list on focus/click. If nothing's loaded yet, fetch
  // the first page (force past minChars) so an empty click still reveals options,
  // approximating the preloaded Customer dropdowns.
  const handleCardFocus = () => {
    if (suggestions.length > 0) { setIsOpen(true); return; }
    fetchSuggestions(inputValue || "", true);
  };

  // Input width is "minimal to accommodate the value":
  //   - explicit `inputWidth` prop wins
  //   - else derive from `maxLength` (each char ≈ 1ch + a little padding)
  //   - else 14ch is a sane default for most IDs (CustID, SalesRepCode, etc.)
  const resolvedInputWidth = inputWidth
    ? inputWidth
    : (maxLength ? `calc(${maxLength}ch + 2.5rem)` : "14ch");

  // A committed selection that isn't being edited renders its code + description
  // on one overlay line inside the box; the raw input text is hidden while it
  // shows so the two can't overlap. Focusing/typing restores the input.
  const showSelectedDisplay =
    !isCard && !!value && !isFocused &&
    !!displayedSecondary && !config?.hideSecondary;

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {isCard ? (
        // Card control — eyebrow label inside a bordered box, the value and the
        // (optional) description beneath, and a chevron. Mirrors SearchableSelect's
        // card variant used on the Customer page.
        <div
          className={"qw-select-card" + (value ? " selected" : "") + (isOpen ? " open" : "")}
          onClick={() => inputRef.current?.focus()}
        >
          {label && <div className="qw-select-eyebrow">{label}</div>}
          <div className="qw-select-row">
            <input
              ref={inputRef}
              className="qw-select-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleCardFocus}
              disabled={disabled}
              placeholder={placeholder}
              required={required}
              maxLength={maxLength || undefined}
              autoComplete="off"
            />
            {displayedSecondary && !config?.hideSecondary && (
              <span className="qw-select-desc" title={displayedSecondary}>{displayedSecondary}</span>
            )}
          </div>
          <span className="qw-select-caret" style={{ cursor: disabled ? "default" : "pointer" }}>
            {isLoading
              ? <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
              : <ChevronDown size={26} />}
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative", flex: "0 0 auto", width: resolvedInputWidth }}>
            <Form.Control
              size={size}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { setIsFocused(true); if (suggestions.length > 0) setIsOpen(true); }}
              onBlur={() => setIsFocused(false)}
              disabled={disabled}
              placeholder={placeholder}
              required={required}
              maxLength={maxLength || undefined}
              autoComplete="off"
              // Hide the raw input text while the committed code+description
              // overlay is shown, so the two never overlap. Editing restores it.
              style={{ paddingRight: 30, ...(showSelectedDisplay ? { color: "transparent" } : null) }}
            />
            {/* Committed selection shown INSIDE the box on one line: the code
                (e.g. the picked Territory ID), then the description. Keeps the
                filter row a constant height (no wrap-below, so the search fields
                never shift) and cannot overlap because the underlying input text
                is hidden while this renders. Suppressed via `hideSecondary: true`
                when a sibling field already shows the same value. pointerEvents:
                none lets a click fall through to focus the field for editing. */}
            {showSelectedDisplay && (
              <div
                style={{
                  position: "absolute", left: 12, right: 30, top: 0, bottom: 0,
                  display: "flex", alignItems: "center", gap: 8,
                  pointerEvents: "none", overflow: "hidden",
                }}
              >
                <span style={{ fontWeight: 600, color: "#0F172A", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                  {inputValue}
                </span>
                <span
                  title={displayedSecondary}
                  style={{
                    color: "#0052A5", fontSize: 12, fontWeight: 500, minWidth: 0,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {displayedSecondary}
                </span>
              </div>
            )}
            {isLoading && (
              <div style={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                pointerEvents: "none"
              }}>
                <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
              </div>
            )}
          </div>
        </div>
      )}
      {isOpen && suggestions.length > 0 && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 2000,
          background: "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "0 0 6px 6px",
          maxHeight: "240px",
          overflowY: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {suggestions.map((item, idx) => {
            const display = pickField(item, config.displayField);
            // secondaryField may be a single string or an array of strings.
            // Arrays are joined with ", " so the dropdown can surface
            // multiple sub-fields (e.g. address1, city, state) on one line.
            let secondary = null;
            if (Array.isArray(config.secondaryField)) {
              secondary = config.secondaryField
                .map((k) => pickField(item, k))
                .filter((v) => v !== undefined && v !== null && v !== "")
                .join(", ");
            } else if (config.secondaryField) {
              secondary = pickField(item, config.secondaryField);
            }
            return (
              <div
                key={idx}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightIdx(idx)}
                style={{
                  padding: isCard ? "10px 16px" : "8px 12px",
                  cursor: "pointer",
                  fontSize: "13px",
                  backgroundColor: idx === highlightIdx ? "#e8f0fe" : "#fff",
                  borderBottom: idx < suggestions.length - 1 ? "1px solid #f0f0f0" : "none",
                  display: "grid",
                  gridTemplateColumns: isCard ? "minmax(110px, auto) minmax(0, 1fr)" : "minmax(92px, auto) minmax(0, 1fr)",
                  gap: 14,
                  alignItems: "baseline",
                }}
              >
                {/* Option A — an aligned code column, not space-between.
                   `justify-content: space-between` put the code at the far
                   left of a 1500px field and its name at the far right, ~1350px
                   apart, with both in identical 700/16px type so neither read
                   as primary. Right-alignment is for NUMBERS scanned down a
                   column; a name belongs beside its code. A shared first track
                   keeps codes aligned vertically — the scannability the old
                   layout was reaching for — while the name starts 14px later.
                   That track is `minmax(floor, auto)`, NOT a fixed width: grid
                   sizes it to the widest code in the list, so a long one like
                   "Individual components" prints in full instead of clipping to
                   "Individual com…", and a list of 5-char territory codes stays
                   tight instead of padding out to a cap nothing reaches. The
                   maxWidth on the span is the backstop — one freak part number
                   can't shove every name across the field, which is the exact
                   complaint this layout was built to answer. */}
                <span style={isCard
                  ? { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600, color: "#4338CA", fontSize: 14, marginLeft: 0, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                  : { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600, color: "#4338CA", fontSize: 12.5, marginLeft: 0, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {display}
                </span>
                {dedupeSecondary(display, secondary) && (
                  <span style={isCard
                    ? { color: "#0F172A", fontSize: 15, marginLeft: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                    : { color: "#475569", fontSize: 12.5, marginLeft: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {dedupeSecondary(display, secondary)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AutocompleteField;
